<template>
  <SideBar />

  <main id="sidenavmain" class="main-content position-relative max-height-vh-100 h-100 border-radius-lg">
    <NavBar />

    <div class="py-4 container-fluid mb-4">
      <div class="col-lg-12 position-relative mt-n3">

        <div class="ts-head d-flex flex-column flex-xl-row justify-content-between align-items-xl-center gap-3 mb-3">
          <div class="ts-head-left d-flex align-items-center gap-2">
            <h6 class="ts-title mb-0">Required subjects</h6>
            <span class="ts-count badge bg-light text-secondary border px-2 py-1" v-if="!isLoading">{{ totalItems }}</span>
            <span class="ts-count badge bg-light text-secondary border px-2 py-1" v-else>...</span>
          </div>
          <div class="ts-head-right d-flex flex-wrap gap-2 justify-content-start justify-content-xl-end align-items-center">
            <!-- Class filter -->
            <select v-model="filterClassId" @change="onFilterClassChange" class="ts-search-input" style="min-width: 180px;">
              <option :value="0">All classes</option>
              <option v-for="c in classOptions" :key="c.id" :value="c.id">{{ c.name }}</option>
            </select>

            <button class="ts-btn-add d-flex align-items-center justify-content-center flex-grow-1 flex-md-grow-0" @click="openAddModal">
              Add New
              <svg width="11" height="11" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24" class="ms-1">
                <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
              </svg>
            </button>
          </div>
        </div>

        <div class="card ts-card">
          <div class="ts-card-body">
            <div v-if="!isLoading && items.length === 0" class="ts-empty">
              <p class="ts-empty-title">No required subjects</p>
              <p class="ts-empty-sub">Mark a subject as required for a class so the system enforces it</p>
            </div>

            <div v-show="isLoading || items.length > 0" class="table-responsive d-none d-md-block">
              <table class="ts-table" data-ts-mobile="cards">
                <thead>
                <tr>
                  <th>Class</th>
                  <th>Subject</th>
                  <th class="text-center" style="width:130px">Actions</th>
                </tr>
                </thead>
                <tbody>
                <template v-if="isLoading">
                  <tr v-for="n in 5" :key="'sk'+n" class="ts-skel-row">
                    <td data-label="Class"><div class="ts-skel" style="width:100px;height:12px"></div></td>
                    <td data-label="Subject"><div class="ts-skel" style="width:140px;height:12px"></div></td>
                    <td data-label="Actions"><div class="ts-skel" style="width:70px;height:28px;border-radius:8px;margin:0 auto"></div></td>
                  </tr>
                </template>
                <template v-else>
                  <tr v-for="item in items" :key="item.id">
                    <td data-label="Class"><strong>{{ item.className }}</strong></td>
                    <td data-label="Subject">{{ item.subName }}</td>
                    <td data-label="Actions">
                      <div class="ts-actions justify-content-center">
                        <button class="ts-btn-suspend" @click="deleteItem(item)" title="Remove">
                          <svg width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                </template>
                </tbody>
              </table>
            </div>

            <div class="d-md-none ts-mobile-list" v-show="isLoading || items.length > 0">
              <template v-if="!isLoading">
                <div v-for="item in items" :key="'m'+item.id" class="ts-card-m">
                  <div class="ts-card-top">
                    <div class="ts-card-info">
                      <div class="ts-name">{{ item.subName }}</div>
                      <div class="ts-email">{{ item.className }}</div>
                    </div>
                  </div>
                  <div class="ts-card-acts">
                    <button class="ts-btn-suspend-full" @click="deleteItem(item)">Remove</button>
                  </div>
                </div>
              </template>
            </div>
          </div>
        </div>

      </div>
    </div>
    <AppFooter />
  </main>

  <!-- ═══ ADD MODAL ═══ -->
  <div v-if="showModal" class="tw-overlay z-index-10000" @mousedown.self="closeModal">
    <div class="tw-modal" style="max-width: 440px;">
      <div class="tw-header">
        <h5 class="tw-title">Mark subject as required</h5>
        <button class="tw-close" @click="closeModal">&times;</button>
      </div>
      <div class="tw-body" style="background: var(--surface-2, #f8fafc);">
        <div class="card p-3 shadow-sm border-0">
          <div class="row g-3">
            <div class="col-12">
              <label class="tw-label">Class <span class="tw-req">*</span></label>
              <select v-model="form.classField" @change="onFormClassChange" class="tw-input">
                <option :value="0" disabled>— Select a class —</option>
                <option v-for="c in classOptions" :key="c.id" :value="c.id">{{ c.name }}</option>
              </select>
            </div>
            <div class="col-12">
              <label class="tw-label">Subject <span class="tw-req">*</span></label>
              <select v-model="form.sub" class="tw-input" :disabled="!form.classField || loadingSubjects">
                <option :value="0" disabled v-if="!form.classField">Select a class first</option>
                <option :value="0" disabled v-else-if="loadingSubjects">Loading...</option>
                <option :value="0" disabled v-else-if="availableSubjects.length === 0">All subjects already required</option>
                <option v-else v-for="s in availableSubjects" :key="s.id" :value="s.id">{{ s.name }}</option>
              </select>
              <p class="text-xs text-muted mb-0 mt-1" v-if="form.classField && availableSubjects.length === 0 && !loadingSubjects">
                Every subject for this class is already marked required.
              </p>
            </div>
          </div>
        </div>
      </div>
      <div class="tw-footer d-flex justify-content-end gap-2 p-3 bg-white border-top">
        <button class="tw-btn tw-btn-ghost" @click="closeModal">Cancel</button>
        <button class="tw-btn tw-btn-primary" @click="submitForm" :disabled="isSubmitting || !form.classField || !form.sub">
          {{ isSubmitting ? 'Saving...' : 'Add' }}
        </button>
      </div>
    </div>
  </div>

</template>

<script>
/* eslint-disable */
import SideBar from "@/fragments/exam/AcademicsSidebar.vue";
import NavBar from "@/fragments/exam/AcademicsNavBar.vue";
import AppFooter from "@/fragments/Footer.vue";
import apiService from '@/apis/apiService';
import swal from 'sweetalert';

export default {
  name: "RequiredSubsPage",
  components: { SideBar, NavBar, AppFooter },

  data() {
    return {
      items: [],
      classOptions: [],
      availableSubjects: [],
      loadingSubjects: false,

      isLoading: true,
      filterClassId: 0,

      showModal: false,
      isSubmitting: false,
      form: { classField: 0, sub: 0 },
    };
  },

  computed: {
    totalItems() { return this.items.length; },
  },

  mounted() {
    this.loadClasses();
    this.fetchItems();
    if (window.innerWidth >= 1024) {
      import('perfect-scrollbar').then((m) => {
        const PS = m.default;
        const c = document.getElementById('sidenavmain');
        if (c) new PS(c);
      });
    }
  },

  methods: {
    /**
     * Classes come from the legacy settings endpoint — still the
     * canonical source. Returns `{no, Class}` rows which we normalise.
     */
    async loadClasses() {
      try {
        const res = await apiService.getDataDynamic('/api/settings/loadclasses');
        const rows = Array.isArray(res) ? res : [];
        this.classOptions = rows
            .filter(r => r && (r.no || r.id))
            .map(r => ({ id: r.no || r.id, name: r.Class || r.class || '' }));
      } catch (e) { /* ignore */ }
    },

    async fetchItems() {
      this.isLoading = true;
      try {
        const params = this.filterClassId ? { classId: this.filterClassId } : {};
        const res = await apiService.apiClient.get('/api/academics/required-subs', { params });
        this.items = Array.isArray(res.data) ? res.data : [];
      } catch (e) {
        this.items = [];
        swal("Error", this.readErr(e, "Could not load"), "error");
      } finally {
        this.isLoading = false;
      }
    },

    onFilterClassChange() {
      this.fetchItems();
    },

    openAddModal() {
      this.form = { classField: 0, sub: 0 };
      this.availableSubjects = [];
      this.showModal = true;
    },

    /**
     * When the user picks a class in the form, load the subjects
     * available to be marked required. The backend endpoint returns
     * only subjects that aren't already required for this class.
     */
    async onFormClassChange() {
      this.form.sub = 0;
      if (!this.form.classField) {
        this.availableSubjects = [];
        return;
      }
      this.loadingSubjects = true;
      try {
        const res = await apiService.apiClient.get('/api/academics/required-subs/subject-options', {
          params: { classId: this.form.classField },
        });
        this.availableSubjects = Array.isArray(res.data) ? res.data : [];
      } catch (e) {
        this.availableSubjects = [];
        swal("Error", this.readErr(e, "Could not load subjects"), "error");
      } finally {
        this.loadingSubjects = false;
      }
    },

    closeModal() {
      this.showModal = false;
    },

    async submitForm() {
      if (!this.form.classField || !this.form.sub) {
        swal("Required", "Pick a class and subject", "warning");
        return;
      }
      this.isSubmitting = true;
      try {
        await apiService.apiClient.post('/api/academics/required-subs', this.form);
        swal("Added", "Subject marked required", "success");
        this.closeModal();
        this.fetchItems();
      } catch (e) {
        swal("Error", this.readErr(e, "Could not save"), "error");
      } finally {
        this.isSubmitting = false;
      }
    },

    async deleteItem(item) {
      const ok = await swal({
        title: "Remove?",
        text: `${item.subName} will no longer be required for ${item.className}.`,
        icon: "warning",
        buttons: ["Cancel", "Remove"],
        dangerMode: true,
      });
      if (!ok) return;
      try {
        await apiService.apiClient.delete(`/api/academics/required-subs/${item.id}`);
        swal("Removed", "Requirement removed", "success");
        this.fetchItems();
      } catch (e) {
        swal("Cannot remove", this.readErr(e, "Failed"), "error");
      }
    },

    readErr(e, fb) { return (e?.response?.data?.querystatus) || fb; },
  },
};
</script>

<style scoped src="@/assets/css/ts-table.css"></style>